import { shopifyComponentCopy } from '@testfeed/product-language'
import { FooterHelp as PageFooterHelp, HelpModal } from '../../shared/PageHelp'
import { Progress } from '../art/Art'
import { HomeArt } from '../art/HomeArt'
import type { BannerSpec } from '../derive'
import { ShopifyPageSkeleton } from '../../ShopifyPageSkeleton'

const HOW_IT_WORKS_MODAL = 'app-home-how-it-works-modal'

function PrivateByDesignBody({ go }: { go: (to: string) => void }) {
  const copy = shopifyComponentCopy.appHome
  return (
    <s-grid gridTemplateColumns="auto 1fr" gap="small-300" alignItems="start">
      <HomeArt name="lock" alt="Private by design: a padlock clicks shut." size={28} background="transparent" />
      <s-grid gap="small-100">
        <s-text type="strong">{copy.trustHeading}</s-text>
        <s-paragraph color="subdued">
          {copy.trustBody} <s-link onClick={() => go('/app/privacy')}>{copy.trustLink}</s-link>
        </s-paragraph>
      </s-grid>
    </s-grid>
  )
}

/**
 * Compact "Private by design" row — framed in a grey box inside the setup guide (see SetupGuide.tsx),
 * or plain once the guide's dismissed and it sits above the footer instead.
 */
export function PrivateByDesign({ go, framed = false }: { go: (to: string) => void; framed?: boolean }) {
  if (framed) {
    return (
      <s-box padding="base" background="subdued" borderRadius="base">
        <PrivateByDesignBody go={go} />
      </s-box>
    )
  }
  return (
    <s-box paddingBlock="base">
      <PrivateByDesignBody go={go} />
    </s-box>
  )
}

export function FooterHelp() {
  const copy = shopifyComponentCopy.appHome
  return (
    <>
      <PageFooterHelp modalId={HOW_IT_WORKS_MODAL} lead={copy.footerLead} link={copy.footerLink} />
      <HelpModal id={HOW_IT_WORKS_MODAL} heading="How TestFeed works">
        <s-paragraph>
          You pick two versions of something on your store, like a photo, a price or a headline. TestFeed shows both to
          simulated shoppers and asks which one they'd buy.
        </s-paragraph>
        <s-paragraph color="subdued">
          Simulated shoppers are built from buying patterns in your store's own order data. We don't collect anything
          beyond what Shopify already has about your store.
        </s-paragraph>
      </HelpModal>
    </>
  )
}

export function HomeBanner({
  spec,
  busy,
  onAction,
  onDismiss,
}: {
  spec: BannerSpec
  busy: boolean
  onAction: (a: NonNullable<BannerSpec['action']>['do']) => void
  onDismiss: () => void
}) {
  return (
    <s-banner
      key={spec.id}
      tone={spec.tone}
      heading={spec.heading}
      dismissible={spec.dismissible}
      onDismiss={onDismiss}
    >
      {spec.body}
      {spec.progress !== undefined && (
        <s-box paddingBlockStart="small">
          <Progress value={spec.progress} label="Resync progress" />
        </s-box>
      )}
      {spec.action && (
        <s-button slot="secondary-actions" variant="secondary" loading={busy} onClick={() => onAction(spec.action!.do)}>
          {spec.action.label}
        </s-button>
      )}
    </s-banner>
  )
}

/** Initial missing query data uses the existing shaped page placeholder. */
export function HomeSkeleton() {
  const copy = shopifyComponentCopy.appHome
  return (
    <s-page>
      <s-query-container>
        <ShopifyPageSkeleton accessibilityLabel={copy.skeletonAriaLabel} />
      </s-query-container>
    </s-page>
  )
}
